import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { PageHeader } from "../../components/StaffLayout";
import { Inp } from "./Enquiries";
import { toast } from "sonner";
import { CheckCircle, ChatTeardropDots, Gear } from "@phosphor-icons/react";

export default function Settings() {
  const [tab, setTab] = useState("clinic");
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => setSettings((await api.get("/settings")).data);
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setBusy(true);
    try {
      const { data } = await api.patch("/settings", patch);
      setSettings(data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (!settings) return <div className="p-12 text-sm text-zinc-500">Loading...</div>;

  return (
    <div data-testid="settings-page">
      <PageHeader title="Settings" subtitle="Configure your clinic, messaging credentials and notifications"/>
      <div className="px-6 sm:px-10 py-8 max-w-4xl">
        <div className="flex border-b border-zinc-200 mb-6">
          {[
            ["clinic","Clinic", Gear],
            ["twilio","Twilio (SMS + WhatsApp)", ChatTeardropDots],
            ["notify","Reminders", CheckCircle],
          ].map(([id, label, Icon]) => (
            <button key={id} onClick={()=>setTab(id)}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 text-sm whitespace-nowrap transition-colors ${
                tab===id ? "border-[#E60000] text-zinc-900 font-bold" : "border-transparent text-zinc-500 hover:text-zinc-900"
              }`} data-testid={`settings-tab-${id}`}>
              <Icon size={16} weight="bold"/> {label}
            </button>
          ))}
        </div>

        {tab === "clinic" && <ClinicTab settings={settings} save={save} busy={busy}/>}
        {tab === "twilio" && <TwilioTab settings={settings} save={save} busy={busy}/>}
        {tab === "notify" && <NotifyTab settings={settings} save={save} busy={busy}/>}
      </div>
    </div>
  );
}

function Card({ children, title, subtitle }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-6 mb-4">
      {title && <h3 className="text-lg font-bold tracking-tight" style={{fontFamily:"Manrope, sans-serif"}}>{title}</h3>}
      {subtitle && <p className="text-sm text-zinc-500 mt-1 mb-4">{subtitle}</p>}
      {children}
    </div>
  );
}

function ClinicTab({ settings, save, busy }) {
  const [form, setForm] = useState({
    clinic_name: settings.clinic_name || "",
    clinic_phone: settings.clinic_phone || "",
    clinic_address: settings.clinic_address || "",
  });
  return (
    <Card title="Clinic details" subtitle="Used on patient communications and invoices.">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2"><Inp label="Clinic name" v={form.clinic_name} onChange={(v)=>setForm({...form,clinic_name:v})} testid="set-cname"/></div>
        <Inp label="Phone" v={form.clinic_phone} onChange={(v)=>setForm({...form,clinic_phone:v})} testid="set-cphone"/>
        <Inp label="Address" v={form.clinic_address} onChange={(v)=>setForm({...form,clinic_address:v})} testid="set-caddr"/>
      </div>
      <div className="mt-5 text-right">
        <button onClick={()=>save(form)} disabled={busy}
          className="bg-[#E60000] hover:bg-[#CC0000] text-white font-semibold rounded-sm px-5 py-2 text-sm disabled:opacity-60" data-testid="set-clinic-save">
          {busy ? "Savingâ€¦" : "Save"}
        </button>
      </div>
    </Card>
  );
}

function TwilioTab({ settings, save, busy }) {
  const [form, setForm] = useState({
    twilio_account_sid: settings.twilio_account_sid || "",
    twilio_auth_token: settings.twilio_auth_token || "",
    twilio_sms_from: settings.twilio_sms_from || "",
    twilio_whatsapp_from: settings.twilio_whatsapp_from || "+14155238886",
    sms_enabled: !!settings.sms_enabled,
    whatsapp_enabled: !!settings.whatsapp_enabled,
    use_sandbox: settings.use_sandbox !== false,
  });
  const [testTo, setTestTo] = useState("");
  const [testCh, setTestCh] = useState("sms");
  const [testBusy, setTestBusy] = useState(false);

  const sendTest = async () => {
    if (!testTo) { toast.error("Enter a recipient number (E.164)"); return; }
    setTestBusy(true);
    try {
      const { data } = await api.post("/settings/test-message", { to: testTo, channel: testCh });
      if (data.sent) toast.success("Test message sent. Check the recipient phone.");
      else toast.error("Twilio rejected â€” check credentials, channel toggle and sender number.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setTestBusy(false); }
  };

  return (
    <>
      <Card title="Twilio credentials"
            subtitle="Get these from console.twilio.com â†’ Dashboard. Account SID + Auth Token unlock messaging. Auth token is stored encrypted and never shown to non-admins.">
        <div className="grid grid-cols-1 gap-3">
          <Inp label="Account SID" v={form.twilio_account_sid} onChange={(v)=>setForm({...form,twilio_account_sid:v})} testid="set-twilio-sid"/>
          <Inp label="Auth Token" type="password" v={form.twilio_auth_token} onChange={(v)=>setForm({...form,twilio_auth_token:v})} testid="set-twilio-token"/>
        </div>
      </Card>

      <Card title="SMS channel" subtitle="Twilio phone number (E.164 e.g. +1415..) used to send SMS reminders.">
        <Toggle label="Enable SMS reminders" v={form.sms_enabled} onChange={(v)=>setForm({...form,sms_enabled:v})} testid="set-sms-toggle"/>
        <div className="mt-3"><Inp label="SMS sender (E.164)" v={form.twilio_sms_from} onChange={(v)=>setForm({...form,twilio_sms_from:v})} testid="set-sms-from"/></div>
      </Card>

      <Card title="WhatsApp channel" subtitle="Twilio WhatsApp sender. The sandbox number +14155238886 is free for testing - recipients must first send 'join &lt;your-code&gt;' to it.">
        <Toggle label="Enable WhatsApp reminders" v={form.whatsapp_enabled} onChange={(v)=>setForm({...form,whatsapp_enabled:v})} testid="set-wa-toggle"/>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Inp label="WhatsApp sender (E.164)" v={form.twilio_whatsapp_from} onChange={(v)=>setForm({...form,twilio_whatsapp_from:v})} testid="set-wa-from"/>
          <Toggle label="Using Twilio sandbox" v={form.use_sandbox} onChange={(v)=>setForm({...form,use_sandbox:v})} testid="set-wa-sandbox"/>
        </div>
      </Card>

      <div className="text-right mb-4">
        <button onClick={()=>save(form)} disabled={busy}
          className="bg-[#E60000] hover:bg-[#CC0000] text-white font-semibold rounded-sm px-5 py-2 text-sm disabled:opacity-60" data-testid="set-twilio-save">
          {busy ? "Savingâ€¦" : "Save Twilio settings"}
        </button>
      </div>

      <Card title="Send a test message" subtitle="Quickly verify your credentials by sending a message to a phone number.">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Inp label="Recipient (E.164)" v={testTo} onChange={setTestTo} testid="set-test-to"/>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-700">Channel</label>
            <select value={testCh} onChange={(e)=>setTestCh(e.target.value)}
              className="mt-2 w-full border border-zinc-300 rounded-sm px-3 py-2 bg-white focus:outline-none focus:border-zinc-900" data-testid="set-test-channel">
              <option value="sms">SMS</option>
              <option value="whatsapp">WhatsApp</option>
            </select>
          </div>
          <div className="flex items-end">
            <button onClick={sendTest} disabled={testBusy}
              className="w-full bg-zinc-900 hover:bg-zinc-800 text-white font-semibold rounded-sm px-4 py-2 text-sm disabled:opacity-60" data-testid="set-test-send">
              {testBusy ? "Sendingâ€¦" : "Send test"}
            </button>
          </div>
        </div>
      </Card>
    </>
  );
}

function NotifyTab({ settings, save, busy }) {
  const [form, setForm] = useState({
    notify_on_booking: settings.notify_on_booking !== false,
    notify_24h_before: settings.notify_24h_before !== false,
    notify_daily_digest: settings.notify_daily_digest !== false,
  });
  return (
    <Card title="Automatic reminders" subtitle="Each trigger fires through both channels you've enabled above.">
      <div className="space-y-3">
        <Toggle label="Booking confirmation â€” sent when an appointment is scheduled" v={form.notify_on_booking} onChange={(v)=>setForm({...form,notify_on_booking:v})} testid="set-notif-booking"/>
        <Toggle label="24-hour appointment reminder â€” runs every 30 min, checks the next-day queue" v={form.notify_24h_before} onChange={(v)=>setForm({...form,notify_24h_before:v})} testid="set-notif-24h"/>
        <Toggle label="Daily digest to staff â€” overdue follow-ups + today's appointments (09:00 IST)" v={form.notify_daily_digest} onChange={(v)=>setForm({...form,notify_daily_digest:v})} testid="set-notif-digest"/>
      </div>
      <div className="mt-5 text-right">
        <button onClick={()=>save(form)} disabled={busy}
          className="bg-[#E60000] hover:bg-[#CC0000] text-white font-semibold rounded-sm px-5 py-2 text-sm disabled:opacity-60" data-testid="set-notif-save">
          {busy ? "Savingâ€¦" : "Save reminders"}
        </button>
      </div>
    </Card>
  );
}

function Toggle({ label, v, onChange, testid }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <input type="checkbox" checked={!!v} onChange={(e)=>onChange(e.target.checked)} className="mt-1" data-testid={testid}/>
      <span className="text-sm text-zinc-700">{label}</span>
    </label>
  );
}
